import React, { useEffect, useRef, useState } from 'react';
import { Button, Header, Image, Placeholder } from 'react-frontier';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { WebProduct } from '../ClassesPublic';
import { Breadcrumbs, Carrousel, Product, Quantity } from '../components';
import { addCommas } from '../UtilPublic';
import { useCart, useTitle } from '../HooksPublic';
import { useTracker } from '@cocoan/components/TrackerHook';
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import API from '../APIPublic';
import classNames from 'classnames';
import slugify from 'slugify';
import Toast from 'react-hot-toast';
import remarkGfm from 'remark-gfm';

import '../style/product_view.scss'

var ProductView = ()=>{
	var { id } = useParams<{ id: string }>();
	var productId = id?.split('-')[0];
	var navigate = useNavigate();
	var { setTitle } = useTitle();
	var { loading: loadingCart, addToCart } = useCart();
	var imagesRef = useRef<HTMLDivElement>(null);
	var Tracker = useTracker();
	var [product, setProduct] = useState<WebProduct>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [quantity, setQuantity] = useState<number>(null);
	var [selectedImage, setSelectedImage] = useState<number>(0);

	useEffect(()=>{
		if(!productId) return;
		var product_id = parseInt(productId);
		if(Number.isNaN(product_id)) return;

		var cancelled = false;
		API.scrollTop();
		setSelectedImage(0);
		setLoadError(null);
		API.getProduct(product_id, true, true, true, true).then(res=>{
			if(cancelled) return;
			if(res.error) return setLoadError(res.message);
			setTitle(res.data.product_name);
			setProduct(res.data);
			var main_cat = res.data.categories?.find(a=>!!a.main);
			navigate({
				pathname: `/product/${res.data.product_id}-${slugify(`${main_cat?.category_name || ''} ${res.data.product_name}`, { lower: true })}`,
				search: window.location.search,
				hash: window.location.hash,
			}, { replace: true });
			if(res.data){
				Tracker.viewContent({
					content_name: res.data.product_name,
					currency: 'MXN',
					value: (res.data.price_discounted || res.data.price),
					items: [{
						item_id: res.data.product_id.toString(),
						item_name: res.data.product_name,
						price: (res.data.price_discounted || res.data.price),
						quantity: 1
					}],
					content_category: res.data.categories && res.data.categories.length>0 ? res.data.categories[0].category_name : '',
					content_type: 'product'
				})
			}
		}).catch(err=>{
			if(!cancelled) setLoadError('Hubo un error inesperado cargando el producto. (LCL-1)');
		});
		return ()=>{ cancelled = true; };
	}, [productId, navigate]);

	var addCart = ()=>{
		addToCart(product.product_id, (quantity || 1)).then(()=>{
			Toast.success('¡Producto agregado!', {
				position: 'top-right'
			})
			Tracker.addToCart({
				content_name: product.product_name,
				currency: 'MXN',
				content_type: 'product',
				items: [{
					item_id: product.product_id.toString(),
					item_name: product.product_name,
					price: (product.price_discounted || product.price),
					quantity: (quantity || 1),
				}],
				value: (product.price_discounted || product.price)*(quantity || 1)
			});
		})
	}

	if(loadError){
		return <Header text='Error' subtext={loadError} style={{ marginTop: 20, fontSize: 40 }} subheaderStyle={{ fontSize: 16 }} />
	}

	if(product===null){
		return <main className="co product-screen placeholder">
			<section className="images" aria-label="Cargando imágenes del producto">
				<div className="item">
					<div className="image"></div>
				</div>
				<div className="item">
					<div className="image"></div>
				</div>
				<div className="item">
					<div className="image"></div>
				</div>
				<div className="item">
					<div className="image"></div>
				</div>
			</section>
			<aside className="information">
				<Placeholder height={25} random />
				<h1 className="name">
					<Placeholder width={240} />
				</h1>
			</aside>
		</main>
	}

	var granel = !!product.price_gram && product.price_gram>0;
	var main_category = product.categories && product.categories.find(a=>!!a.main);
	var hasMultipleImages = product.images && product.images.length > 1;

	return <main className="co product-view">
		<section className={classNames("co product-screen", {
			placeholder: product===null,
			single: !product.images || product.images.length<=1,
			double: product.images && product.images.length===2,
			'gallery-mode': hasMultipleImages,
		})}>
			{hasMultipleImages ? (
				<div className="gallery-layout">
					<div className="gallery-thumbnails">
						{product.images.map((a, i) => (
							<div
								className={classNames("thumb-item", { active: selectedImage === i })}
								key={`thumb-${i}`}
								onClick={() => setSelectedImage(i)}
							>
								<Image src={a} fallback='/placeholder_portrait.webp' />
								{selectedImage === i && <div className="thumb-overlay" />}
							</div>
						))}
					</div>
					<div className="gallery-main">
						<Image src={product.images[selectedImage]} fallback='/placeholder_portrait.webp' />
					</div>
				</div>
			) : (
				<section className="images" ref={imagesRef} aria-label="Galería de imágenes del producto">
					{product.images.map((a, i)=>(
						<div className="item" key={`prdimg-${i}`}>
							<div className="image">
								<Image src={a} fallback='/placeholder_portrait.webp' />
							</div>
						</div>
					))}
				</section>
			)}
			<aside className="information">
				{!!main_category && (
					<Breadcrumbs items={[
						{ text: 'Shop', as: Link, to: '/shop' },
						{ text: main_category.category_name, as: Link, to: `/shop/${main_category.category_id}-${slugify(main_category.category_name).toLowerCase()}`, style: { fontWeight: 'bold' } }
					]} />
				)}
				<h1 className="name">{product.product_name}</h1>
				{!!product.message && (
					<div className="tags">
						<div className="tag">{product.message}</div>
					</div>
				)}
				<div className="price">
					{product.price_discounted ? <>
						<div className="final">
							${addCommas(product.price_discounted, true, true)}
						</div>
						<div className="discounted">
							${addCommas(product.price, true, true)}
						</div>
					</> : (
						`$${addCommas(product.price, true, true)}`
					)}
					{!!granel && (
						<span style={{ color: 'gray', fontSize: 12, marginLeft: 5, fontWeight: 'normal', lineHeight: '18px' }}> /100 gramos</span>
					)}
				</div>
				<div className="section" style={{ color: '#828282' }}>
					<ReactMarkdown className='description' linkTarget={'_blank'} remarkPlugins={[remarkGfm]} children={product.description} /> 
				</div>
				<div className="cart-actions">
					<Quantity quantity={(quantity || 1)} onChange={setQuantity} format={granel ? v=>`${v*100} gr` : null} />
					<Button fluid color="black" className="add" text="Agregar al carrito" loading={loadingCart} onClick={addCart}/>
				</div>
			</aside>
		</section>
		{product.related && product.related.length > 0 && (
			<section style={{ marginTop: 100 }} className="co product-related">
				<Carrousel title="También podría gustarte">
					{product.related.map(r => (
						<Product product={r} key={`rel-${r.product_id}`} />
					))}
				</Carrousel>
			</section>
		)}
	</main>
}

export default ProductView;